import { Component, OnInit, OnDestroy, ElementRef, ChangeDetectorRef, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { UIComponentsModule } from 'src/app/shared/ui-components/ui-components.module';
import * as d3 from 'd3';
import { ALL_CATEGORIES, getCategoryColor, calculateBaselineEmissions, calculateDailyEmissions } from 'src/app/shared/charts/chart-data.util';
import { CategoryData } from 'src/app/shared/models/interfaces';
import { UserService } from 'src/app/core/services/user/user.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-dashboard-chart',
  imports: [UIComponentsModule],
  templateUrl: './dashboard-chart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./dashboard-chart.component.scss']
})
export class DashboardChartComponent implements AfterViewInit, OnDestroy {
  private svg: any;
  private chartMargin = { top: 10, right: 10, bottom: 10, left: 10 };
  private subscription = new Subscription();
  private resizeObserver: ResizeObserver;
  
  todayEmissions: CategoryData[] = [];
  totalEmissions: number = 0;
  hasData: boolean = false;
  reportData: any = null;
  isReportDataAvailable: boolean = false;
  
  constructor(
    private elementRef: ElementRef,
    private userService: UserService,
  ) {
    this.resizeObserver = new ResizeObserver(() => {
      this.generateChart();
    });
  }

  ngAfterViewInit(): void {
    this.loadEmissionsReport();
    // Start observing after component initialization
    const container = this.elementRef.nativeElement.querySelector('.dashboard-chart');
    if (container) {
      this.resizeObserver.observe(container);
    }
  }

  ngOnDestroy(): void {
    if (this.svg) {
      this.svg.remove();
    }
    this.resizeObserver.disconnect();
    this.subscription.unsubscribe();
  }

  private loadEmissionsReport(): void {
    this.subscription.add(
      this.userService.getMyEmissionsReport().subscribe({
        next: (data) => {
          this.reportData = data;
          this.calculateTodayEmissions();
          this.checkReportDataAvailability();
          if (this.hasData) {
            setTimeout(() => {
              this.generateChart();
            }, 0);
          }
        },
        error: (error) => {
          console.error('Error loading emissions report:', error);
        }
      })
    );
  }

  private calculateTodayEmissions() {
    if (!this.reportData) return;

    const today = new Date();
    const dailyEmissions = calculateDailyEmissions(this.reportData, today);
    const baselineEmissions = calculateBaselineEmissions(this.reportData);
    
    // Separate baseline and daily emissions for proper ordering
    const baselineCategories = ALL_CATEGORIES
      .filter(cat => cat.key.startsWith('baseline'))
      .map(category => ({
        ...category,
        emissions: baselineEmissions[category.key] || 0
      }))
      .filter(cat => cat.emissions > 0)
      .reverse();
    
    const dailyCategories = ALL_CATEGORIES
      .filter(cat => cat.key.startsWith('daily'))
      .map(category => ({
        ...category,
        emissions: dailyEmissions[category.key] || 0
      }))
      .filter(cat => cat.emissions > 0)
      .reverse();

    this.todayEmissions = [...dailyCategories, ...baselineCategories];
    
    this.totalEmissions = this.todayEmissions.reduce((sum, cat) => sum + cat.emissions, 0);
    this.hasData = this.totalEmissions > 0;
  }

  private checkReportDataAvailability() {
    this.isReportDataAvailable =
      !!this.reportData &&
      (this.reportData.activity.length > 0 ||
        this.reportData.baseline.length > 0 ||
        this.reportData.food.length > 0 ||
        this.reportData.flight.length > 0 ||
        this.reportData.mobility.length > 0 ||
        this.reportData.product.length > 0 ||
        this.reportData.constants.length > 0);
  }

  private generateChart(): void {
    const container = this.elementRef.nativeElement.querySelector('.dashboard-chart');
    if (!container || !this.todayEmissions.length) return;

    d3.select(container).selectAll('*').remove();
    
    const containerWidth = container.clientWidth || 50;
    const containerHeight = container.clientHeight || 200;
    const chartWidth = containerWidth - this.chartMargin.left - this.chartMargin.right;
    const chartHeight = containerHeight - this.chartMargin.top - this.chartMargin.bottom;

    this.svg = d3.select(container)
      .append('svg')
      .attr('width', containerWidth)
      .attr('height', containerHeight);

    const g = this.svg.append('g')
      .attr('transform', `translate(${this.chartMargin.left},${this.chartMargin.top})`);

    // create clipping path for rounded corners on entire bar
    const defs = this.svg.append('defs');
    defs.append('clipPath')
      .attr('id', 'dashboard-rounded-corner')
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', chartWidth)
      .attr('height', chartHeight)
      .attr('rx', 5)
      .attr('ry', 5);

    // create vertical stacked bar
    const yScale = d3.scaleLinear()
      .domain([0, this.totalEmissions])
      .range([0, chartHeight]);

    let currentY = 0;

    g.selectAll('.segment')
      .data(this.todayEmissions)
      .enter().append('rect')
      .attr('class', 'segment')
      .attr('x', 0)
      .attr('y', (d: any) => {
        const y = currentY;
        currentY += yScale(d.emissions);
        return y;
      })
      .attr('width', chartWidth)
      .attr('height', (d: any) => yScale(d.emissions))
      .attr('fill', (d: any) => getCategoryColor(d.key))
      .attr('clip-path', 'url(#dashboard-rounded-corner)');
  }
}